import { AnyAction } from 'redux';
import { call, CallEffect, ForkEffect, put, PutEffect, takeLatest } from 'redux-saga/effects';
import { OrganizationsActionType } from './enum';
import { handleEmptyNullValues } from '@utilities';
import {
	addDepartment,
	addUser,
	autocompleteManager,
	fetchDepartments,
	fetchDesignations,
	getAllUsers,
	updateUserStatus,
	updateEmployeeDetails,
	deleteEmployee,
	getUserById,
	getAssignedProjects
} from './api';

type OrganizationSagaEffect = Generator<CallEffect<any> | PutEffect<any>>;
type OrganizationSagaForEffect = Generator<ForkEffect<void>>;

export function* fetchAllUsersEffect(action: AnyAction): OrganizationSagaEffect {
	const { filters, successCB, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getAllUsers, handleEmptyNullValues(filters));
		const usersList = data.users;
		yield put({
			type: OrganizationsActionType.STORE_ALL_USERS,
			payload: { usersList, totalRecords: data.totalRecords }
		});
		if (successCB) successCB(data);
	} catch (error: unknown) {
		yield put({
			type: OrganizationsActionType.ERROR_STORE_ALL_USERS,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* updateUserStatusEffect(action: AnyAction): OrganizationSagaEffect {
	const { userId, status, successCB, errorCB } = action.payload;
	try {
		yield call(updateUserStatus, { userId, status });
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* addUserEffect(action: AnyAction): OrganizationSagaEffect {
	const { formData, successCB, errorCB, finalCB } = action.payload;
	const data = {
		...formData,
		managerId: formData?.managerId?.userId,
		leadSource:
			formData?.department === 'SALES'
				? (formData?.leadSource || []).map((source: any) => source?.value || source)
				: []
	};
	try {
		const response = yield call(addUser, handleEmptyNullValues(data));
		successCB(response.data);
	} catch (error: unknown) {
		// `post()` rejects with e?.response?.data so error IS the response body
		const code = error?.code;
		const message = error?.message;
		errorCB({ code, message });
	} finally {
		finalCB();
	}
}

export function* fetchDepartmentsEffect(action: AnyAction): OrganizationSagaEffect {
	const { errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchDepartments);
		yield put({ type: OrganizationsActionType.STORE_ALL_DEPARTMENTS, payload: data.departments });
	} catch (error) {
		errorCB();
		yield put({
			type: OrganizationsActionType.ERROR_STORE_ALL_DEPARTMENTS,
			payload: {
				error
			}
		});
	}
}
export function* fetchDesignationsEffect(action: AnyAction): OrganizationSagaEffect {
	const { errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchDesignations);

		yield put({ type: OrganizationsActionType.STORE_ALL_DESIGNATIONS, payload: data.designations });
	} catch (error) {
		errorCB();
		yield put({
			type: OrganizationsActionType.ERROR_STORE_ALL_DESIGNATIONS,
			payload: {
				error
			}
		});
	}
}

export function* addDepartmentEffect(action: AnyAction): OrganizationSagaEffect {
	const { formData, successCB, errorCB, finalCB } = action.payload;
	try {
		yield call(addDepartment, formData);
		successCB();
	} catch (err: unknown) {
		errorCB(err.message);
		yield put({ type: OrganizationsActionType.ERROR_ADD_DEPARTMENT });
	} finally {
		finalCB();
	}
}

export function* fetchManagerAutocompleteEffect(action: AnyAction): OrganizationSagaEffect {
	const { prefix, department, designation, errorCB } = action.payload;

	try {
		if (designation === '') {
			yield put({ type: OrganizationsActionType.STORE_MANAGER_AUTOCOMPLETE, payload: { data: [] } });
			return;
		}
		const { data }: any = yield call(autocompleteManager, prefix, department, designation);
		const ManagersList = data.users?.map((manager: Record<string, any>) => {
			return {
				...manager,
				displayName: `${manager.fullName} (${manager.phoneNumber})`
			};
		});
		yield put({ type: OrganizationsActionType.STORE_MANAGER_AUTOCOMPLETE, payload: { data: ManagersList } });
	} catch (error: unknown) {
		yield put({
			type: OrganizationsActionType.ERROR_MANAGER_AUTOCOMPLETE,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* updateEmployeeDetailsEffect(action: AnyAction): OrganizationSagaEffect {
	const { userId, formData, successCB, errorCB } = action.payload;
	try {
		yield call(updateEmployeeDetails, userId, formData);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchUserByIdEffect(action: AnyAction): OrganizationSagaEffect {
	const { userId, successCB, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getUserById, userId);
		if (successCB) successCB(data);
	} catch (error: unknown) {
		if (errorCB) errorCB(error?.message);
	}
}

export function* fetchAssignedProjectsEffect(action: AnyAction): OrganizationSagaEffect {
	const { userId, successCB, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getAssignedProjects, userId);
		if (successCB) successCB(data);
	} catch (error: unknown) {
		if (errorCB) errorCB(error?.message);
	}
}

export function* deleteEmployeeEffect(action: AnyAction): OrganizationSagaEffect {
	const { userId, replacementUserId, successCB, errorCB, finalCB } = action.payload;
	try {
		yield call(deleteEmployee, userId, replacementUserId);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* fetchAllUsersSaga(): OrganizationSagaForEffect {
	yield takeLatest(OrganizationsActionType.FETCH_ALL_USERS, fetchAllUsersEffect);
}

export function* updateUserStatusSaga(): OrganizationSagaForEffect {
	yield takeLatest(OrganizationsActionType.UPDATE_USER_STATUS, updateUserStatusEffect);
}

export function* addUserSaga(): OrganizationSagaForEffect {
	yield takeLatest(OrganizationsActionType.ADD_USER, addUserEffect);
}

export function* fetchDepartmentsSaga(): OrganizationSagaForEffect {
	yield takeLatest(OrganizationsActionType.FETCH_ALL_DEPARTMENTS, fetchDepartmentsEffect);
}

export function* fetchDesignationsSaga(): OrganizationSagaForEffect {
	yield takeLatest(OrganizationsActionType.FETCH_ALL_DESIGNATIONS, fetchDesignationsEffect);
}

export function* addDepartmentSaga(): OrganizationSagaForEffect {
	yield takeLatest(OrganizationsActionType.ADD_DESIGNATION, addDepartmentEffect);
}

export function* fetchManagerAutocompleteSaga(): OrganizationSagaForEffect {
	yield takeLatest(OrganizationsActionType.FETCH_MANAGER_AUTOCOMPLETE, fetchManagerAutocompleteEffect);
}

export function* updateEmployeeDetailsSaga(): OrganizationSagaForEffect {
	yield takeLatest(OrganizationsActionType.UPDATE_EMPLOYEE_DETAILS, updateEmployeeDetailsEffect);
}

export function* deleteEmployeeSaga(): OrganizationSagaForEffect {
	yield takeLatest(OrganizationsActionType.DELETE_EMPLOYEE, deleteEmployeeEffect);
}

export function* fetchUserByIdSaga(): OrganizationSagaForEffect {
	yield takeLatest(OrganizationsActionType.FETCH_USER_BY_ID, fetchUserByIdEffect);
}

export function* fetchAssignedProjectsSaga(): OrganizationSagaForEffect {
	yield takeLatest(OrganizationsActionType.FETCH_ASSIGNED_PROJECTS, fetchAssignedProjectsEffect);
}
